/**
 * JobRunSummary — Himaad's Section 3 drawer body: ONE BIG METRIC, a subline that
 * makes it mean something, and a definition list under it.
 *
 * Replaces `JobMetadataBlocks`' 2×3 grid of tinted blocks. The grid gave six
 * facts equal weight, so the reader had to work out which one answered the
 * question they opened the drawer with. Every frame in Himaad's Section 3 leads
 * with the single number that IS the answer for that state:
 *
 *   | state         | metric        | why it is the answer                     |
 *   |---------------|---------------|------------------------------------------|
 *   | running       | elapsed       | "how long has this been going?"          |
 *   | ready         | row count     | "did it return anything, and how much?"  |
 *   | didn't finish | duration, red | the duration IS the diagnosis (see below)|
 *   | scheduled     | next run time | "when does this happen?"                 |
 *   | expired       | row count     | what it returned, before the blob went   |
 *
 * The last two have no frame. Himaad drew three states; the drawer has six, and
 * absence in a mock is not a removal — so they extend the pattern rather than
 * fall back to the old grid. Cancelled is folded in with a stopped-by-you line.
 *
 * ## Why "6m 00s" in red is a diagnosis and not just a failure
 *
 * `MAX_RUN_MS` is the runner's cap, mirroring CB's own ~300s SQL CommandTimeout.
 * A report that ran the full six minutes and returned nothing did not error — it
 * was stopped. The duration is the only thing on screen that explains why there
 * are no rows, so it leads. Every number here is derived from the constant, never
 * typed, so changing the cap changes the copy (CC-50152 is the open decision on
 * whether 6 minutes is the number; this file must not be a second place to edit).
 *
 * ## Retention: what this may and may not say
 *
 * Himaad's Ready frame says "kept until tomorrow 2:59 PM". That is false three
 * ways and the fix is not cosmetic:
 *
 *   1. Ad-hoc results live ONE WEEK, not 24 hours (`RESULT_TTL_HOURS = 24*7`).
 *   2. The clock runs from CREATION, not from finish.
 *   3. A SCHEDULE-produced result has no row expiry — the server writes
 *      `expires_at = null` and prunes by COUNT (`retain_count`, default 5).
 *      Inventing a date from cadence × count would be a prediction rendered as a
 *      stored fact that breaks the moment the schedule is paused or edited.
 *
 * ⚠ Spec 152 corrected point 3's old ending ("no time-based expiry at all"): the
 * results BUCKET deletes every object by age (about 7 days on QA, 2 on prod), so a
 * schedule's run is kept by count AND for at most that age. The API now says when
 * (`resultExpiresAt`) and how long (`resultMaxAgeDays`), and every sentence about it
 * comes from `resultRetention.js` — this file renders that module's clause.
 *
 * So: an absolute expiry ONLY where the run actually carries one, the count rule
 * stated as a count where it applies, the bucket's age where the API declares it.
 * This is why `scheduleId` had to reach the client at all — without it no sentence
 * here can tell the two populations apart, and one of them gets a lie either way.
 *
 * ## What is deliberately NOT here
 *
 * "2s in the queue" (both of Himaad's non-scheduled frames show it). There is no
 * `started_at` column, so no queue time is recorded to print. ⚠ Spec 178
 * (CC-54221) changed what the two columns this file reads mean, for a GENERIC run
 * that starts after it: cb-api resets `created_at` to the moment the run STARTS, so
 * "Started" is the start and `duration_ms` is the run alone — the wait at the firm's
 * cap is no longer folded into it, and is not stored anywhere. A specialized run,
 * and any row written before that change, still counts from when it was booked.
 */
import { MAX_RUN_MS } from '@config/queueConfig'
import { fmtDuration, fmtElapsed, fmtRowCount, fmtRows, fmtTime, fmtWhen } from './queueFormat'
import { jobTrigger } from './jobIdentity'
import FirmText from './FirmText'
import { runVisibility } from './jobVisibility'
import { retentionClause } from './resultRetention'
import { deliveryOutcome, recipientOutcome, RECIPIENTS_UNREADABLE_NOTE } from './deliveryOutcome'
import { PENDO_IGNORE } from '@/analytics/domPrivacy'

const CAP_MINUTES = Math.round(MAX_RUN_MS / 60000)

/**
 * One label/value pair of the definition list.
 *
 * `tone: 'danger'` colours BOTH halves, because on the one row that uses it (a
 * refused email) the reason is the payload and rendering it in the same grey as
 * "queued for the next delivery pass" is how a failure reads as routine.
 * `--color-rc-danger-text` is #b91c1c — **6.47:1 on white** (re-measured
 * 2026-09-08; this line said 5.9:1, which was wrong in the safe direction), so it
 * clears SC 1.4.3 at this size where `text-red-500` (3.76:1) would not.
 *
 * Type sizes here are spec 119 / C2's normalised scale (13px label, 14px value,
 * 13px note) — see that spec for the whole before/after table and for why the
 * fractional sizes this file used to carry were collapsed rather than bumped.
 */
const Fact = ({ label, value, note, tone }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-gray-100 py-2 last:border-b-0">
    <dt className="shrink-0 text-[13px] font-medium uppercase tracking-[0.4px] text-gray-500">{label}</dt>
    <dd className={`min-w-0 text-right text-[14px] ${tone === 'danger' ? 'text-rc-danger-text' : 'text-gray-800'}`}>
      {value}
      {note && (
        <span className={`block text-[13px] ${tone === 'danger' ? 'text-rc-danger-text' : 'text-gray-500'}`}>
          {note}
        </span>
      )}
    </dd>
  </div>
)

const JobRunSummary = ({ job, previousRun, runByName, now = 0 }) => {
  if (!job) return null

  const state = job.effectiveState || job.state
  const running = state === 'queued' || state === 'running'
  const failed = state === 'failed'
  const expired = state === 'expired'
  const cancelled = state === 'cancelled'
  const scheduled = state === 'scheduled' || state === 'recurring'
  // `!== false` so an absent flag still means "mine" — the flag only exists in a
  // widened (firm/group) listing.
  const owned = job.isOwner !== false
  // Spec 132 — ONE answer to "where did this run come from", which decides the Trigger
  // line AND which retention rule applies. It can now also say "Not recorded": a
  // payload with no schedule key at all is not a manual run (jobIdentity.jobTrigger).
  const trigger = jobTrigger(job)
  // Spec 137 — null unless this is the owner's run and they asked for an email.
  const email = deliveryOutcome(job, state)

  // Did this run hit the cap? Within a couple of seconds is the cap, not a
  // coincidence — a run that stopped at 5m 58s stopped because of the ceiling.
  const hitCap = job.durationMs != null && job.durationMs >= MAX_RUN_MS - 2000

  let metric
  let metricTone = 'text-cb-navy'
  let subline

  if (running) {
    metric = fmtElapsed(job.createdAt, now) || '—'
    subline = `elapsed · stops automatically at ${fmtDuration(MAX_RUN_MS)}`
  } else if (scheduled) {
    metric = job.scheduledFor ? fmtTime(job.scheduledFor) : '—'
    subline = job.scheduledFor ? `${fmtWhen(job.scheduledFor, now)} · not run yet` : 'waiting to run'
  } else if (failed) {
    metric = fmtDuration(job.durationMs)
    metricTone = 'text-rc-danger-text'
    subline = hitCap
      ? `ran, then stopped at the ${CAP_MINUTES}-minute cut-off with no rows`
      : 'stopped without returning rows'
  } else if (cancelled) {
    metric = fmtDuration(job.durationMs)
    subline = owned ? 'stopped by you · the result was discarded' : 'stopped · the result was discarded'
  } else {
    // ready and expired both led with what the run RETURNED. On an expired row
    // the metadata outlives the blob, and the row count is the part worth keeping.
    metric = fmtRowCount(job.rowCount)
    const returned = job.durationMs != null ? `returned in ${fmtDuration(job.durationMs)}` : null
    subline = [returned, retentionClause({ job, expired, trigger })].filter(Boolean).join(' · ')
  }

  const rowDelta = previousRun?.rowCount != null && job.rowCount != null
    ? job.rowCount - previousRun.rowCount
    : null

  return (
    <div>
      <p className={`text-[28px] font-display leading-none tabular-nums ${metricTone}`} data-testid="job-metric">
        {metric}
      </p>
      <p className="mt-1.5 text-[14px] text-gray-600" data-testid="job-metric-subline">{subline}</p>

      <dl className="mt-4">
        {/* Started, with no queue-wait clause — see the header note. */}
        <Fact
          label={scheduled ? 'Runs' : 'Started'}
          value={scheduled
            ? (job.scheduledFor ? fmtTime(job.scheduledFor) : '—')
            : fmtTime(job.createdAt)}
        />
        {!running && !scheduled && (
          <Fact
            label={failed || cancelled ? 'Stopped' : 'Finished'}
            value={fmtTime(job.finishedAt ?? job.updatedAt)}
          />
        )}
        {/* Rows earns a row of its own only when it is NOT already the metric,
            i.e. everywhere except ready/expired. Restating the headline number
            four lines below itself is what the old grid did. */}
        {!running && !scheduled && (failed || cancelled) && (
          <Fact label="Rows" value={fmtRows(job.rowCount ?? 0)} />
        )}
        {rowDelta != null && !failed && !cancelled && (
          <Fact
            label="vs last run"
            value={rowDelta === 0
              ? 'same as last run'
              : `${fmtRows(Math.abs(rowDelta))} ${rowDelta > 0 ? 'more' : 'fewer'}`}
          />
        )}
        {/*
          * There is deliberately no name for a teammate: the list route strips
          * `user_id` and serves only a boolean, so "A teammate" is the most this
          * can honestly say. CC-50231 removed a hardcoded "You" that asserted
          * ownership over colleagues' runs — do not reintroduce an identity here
          * without a privacy decision.
          */}
        <Fact label="Run by" value={runByName ?? (owned ? 'You' : 'A teammate')} />
        {/* Spec 216: "from <schedule name>" — an Advanced Report schedule is named after the
            firm's report by default, so its name is kept out of Pendo. */}
        <Fact label="Trigger" value={trigger.label} note={trigger.note && <FirmText reportId={job.reportId}>{trigger.note}</FirmText>} />
        {/*
          * Spec 167 — the run's REAL audience, in two halves (`jobVisibility.js`).
          * This read "Only me" on every owned run, and that was false: the Queue's
          * "Everyone at the firm" view lists every run at the firm, whatever its
          * schedule's own "Who can see this" says — only the RESULT stays the
          * owner's. Measured on QA, 2026-10-12; the evidence is in that module.
          */}
        <Fact label="Visibility" {...runVisibility(job)} />
        {/*
          * Email, and ONLY when the owner asked for one (QA, 2026-09-03).
          *
          * Spec 137 decision 10: the line is driven by the stored delivery STATUS
          * (accepted / unconfirmed / failed / skipped), never by `deliveredAt`,
          * which only means "finished" — the old precedence labelled a ten-try
          * give-up "Sent to you". `deliveryOutcome.js` owns the wording and why
          * nothing here may say "delivered".
          *
          * Rendered only for the owner: `deliver_email` is the owner's own request
          * and its outcome is not a colleague's business, and on a foreign row the
          * reader could do nothing about it anyway (the list route strips the
          * fields from a foreign row too).
          */}
        {email && (
          <Fact label="Email" value={email.value} note={email.note} tone={email.tone} />
        )}
        {/*
          * Spec 138 §3.7 — one line per OTHER recipient of a scheduled run, under the
          * Email line: the address, then its own outcome in the same vocabulary. Same
          * gate as the Email row (the owner's run, email asked), and the API sends the
          * list on owned rows only, so a colleague's run can never show it.
          */}
        {email && (job.deliveryRecipients || []).map((r) => {
          const line = recipientOutcome(r, job, state)
          if (!line) return null
          // A third party's address: kept out of Pendo's text capture (spec 094, *review*).
          return (
            <Fact
              key={r.to}
              label="Also to"
              value={<span className={`${PENDO_IGNORE} break-all`}>{r.to}</span>}
              note={<>{line.value}{line.note ? <> — {line.note}</> : null}</>}
              tone={line.tone}
            />
          )
        })}
        {email && job.deliveryRecipientsUnreadable && (
          <Fact label="Also to" value="Unknown" note={RECIPIENTS_UNREADABLE_NOTE} />
        )}
      </dl>
    </div>
  )
}

/*
 * The retention clause moved to `resultRetention.js` (spec 152), with the rest of the
 * app's retention sentences — this file and the expired panel each carried their own
 * wording, and the one here promised "kept while it is one of this schedule's most
 * recent runs" under a Ready chip for runs the results bucket had already deleted.
 * Every rule that stood here (spec 132's two included) is kept there, now bounded by
 * the bucket's age: see `retentionClause`'s note.
 */

export default JobRunSummary
